/**
 * Coach Engagement List — Drill-down view
 *
 * Shows a paginated list of coaches in a specific engagement category.
 * Navigated to from the Admin Dashboard "Coach Engagement" section.
 *
 * ── Category Definitions (edit CATEGORIES to change) ─────────────
 *  Recruiting  — sent ≥1 contact to a player
 *  Engaged     — favorited/compared + viewed/searched
 *  Browsing    — viewed or searched only (no deeper actions)
 *  Inactive    — no events in the selected time range
 *
 * ── Health Score (0–100, capped) ─────────────────────────────────
 *  +10 search_executed  |  +10 view_player_profile
 *  +30 add_favorite / compare_add  |  +50 contact_player_sent
 */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import Loader from '../shared/displayComponents/loader/Loader';
import url from '../constants/config';

import './CoachEngagementList.scss';

/* ── Configuration (single source of truth for labels/colors) ───── */

const CATEGORIES = [
  { key: 'all', label: 'All', desc: 'All registered coaches', color: '#6366f1', icon: 'fa-users' },
  { key: 'recruiting', label: 'Recruiting', desc: 'Sent ≥1 contact', color: '#10b981', icon: 'fa-paper-plane' },
  { key: 'engaged', label: 'Engaged', desc: 'Favorited / compared + viewed', color: '#f59e0b', icon: 'fa-star' },
  { key: 'browsing', label: 'Browsing', desc: 'Viewed / searched only', color: '#3b82f6', icon: 'fa-eye' },
  { key: 'inactive', label: 'Inactive', desc: 'No activity in period', color: '#94a3b8', icon: 'fa-moon' },
];

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map(c => [c.key, c]));

const RANGE_OPTIONS = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

/* ── Helpers ─────────────────────────────────────────────────────── */

function healthLabel(score) {
  if (score === 0) return { text: 'None', cls: 'none' };
  if (score <= 20) return { text: 'Low', cls: 'low' };
  if (score <= 50) return { text: 'Moderate', cls: 'moderate' };
  if (score <= 80) return { text: 'Active', cls: 'active' };
  return { text: 'Very Active', cls: 'high' };
}

const timeAgo = dateStr => {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const PAGE_SIZE = 25;

/* ── Component ───────────────────────────────────────────────────── */

const CoachEngagementList = () => {
  const { userRole } = useSelector(({ authorization }) => authorization);
  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const [category, setCategory] = useState(params.get('category') || 'all');
  const [range, setRange] = useState(params.get('range') || '30d');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const token = localStorage.getItem('authorization');
  const headers = { Authorization: `Bearer ${token}` };

  const catInfo = CATEGORY_MAP[category] || CATEGORY_MAP.recruiting;

  /* Fetch summary counts for all 4 cards */
  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(
        `${url}/admin/coach-engagement/summary?range=${range}`,
        { headers },
      );
      setSummary(res.data);
    } catch (err) {
      console.error('Engagement summary fetch error', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  /* Fetch paginated list for the selected category */
  const fetchList = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const res = await axios.get(
        `${url}/admin/coach-engagement/list?category=${category}&range=${range}&limit=${PAGE_SIZE}&page=${p}${searchParam}`,
        { headers },
      );
      setData(res.data);
      setPage(p);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Engagement list fetch error', err);
      setError(err.response?.data?.message || 'Failed to load coaches.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, range, search]);

  useEffect(() => {
    if (userRole !== 'admin') {
      history.push('/available-players');
      return;
    }
    fetchSummary();
    fetchList(1);
  }, [fetchSummary, fetchList, userRole, history]);

  // Keep URL in sync with state
  useEffect(() => {
    const sp = new URLSearchParams();
    sp.set('category', category);
    sp.set('range', range);
    history.replace({ search: sp.toString() });
  }, [category, range, history]);

  const pageInfo = data?.pageInfo || {};
  const items = data?.items || [];
  const totalPages = pageInfo.totalPages || 1;

  const handleCategoryClick = (key) => {
    setCategory(key);
    setPage(1);
  };

  return (
    <div className="coach-engagement">
      {/* Header */}
      <div className="coach-engagement__header">
        <div>
          <h1 className="coach-engagement__title">
            <i className="fas fa-users-cog" /> Coach Engagement
          </h1>
          <p className="coach-engagement__desc">
            Breakdown of coach activity for the selected period
          </p>
        </div>
        <div className="coach-engagement__controls">
          <div className="coach-engagement__range">
            {RANGE_OPTIONS.map(r => (
              <button
                key={r.value}
                type="button"
                className={`coach-engagement__range-btn ${range === r.value ? 'coach-engagement__range-btn--active' : ''}`}
                onClick={() => { setRange(r.value); setPage(1); }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="coach-engagement__summary-cards">
          {CATEGORIES.map(cat => {
            const count = summary.counts?.[cat.key] || 0;
            const total = summary.counts?.totalCoaches || 0;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            const isActive = category === cat.key;
            return (
              <button
                key={cat.key}
                type="button"
                className={`coach-engagement__summary-card ${isActive ? 'coach-engagement__summary-card--active' : ''}`}
                onClick={() => handleCategoryClick(cat.key)}
              >
                <div className="coach-engagement__summary-dot" style={{ background: cat.color }} />
                <div className="coach-engagement__summary-value">{count}</div>
                <div className="coach-engagement__summary-label">{cat.label}</div>
                <div className="coach-engagement__summary-pct">{pct}%</div>
                <div className="coach-engagement__summary-desc">{cat.desc}</div>
              </button>
            );
          })}
        </div>
      )}

      {summary && summary.counts?.totalCoaches > 0 && (summary.counts?.recruiting || 0) === 0 && (summary.counts?.engaged || 0) === 0 && (
        <p className="coach-engagement__hint">
          <span role="img" aria-label="warning">⚠️</span>
          No coaches are actively recruiting or engaged — they may need guidance.
        </p>
      )}

      {/* Search bar */}
      <div className="coach-engagement__search-bar">
        <div className="coach-engagement__search-input-wrap">
          <i className="fas fa-search coach-engagement__search-icon" />
          <input
            type="text"
            className="coach-engagement__search-input"
            placeholder="Search by name, email, or school…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                setSearch(searchInput);
                setPage(1);
              }
            }}
          />
          {searchInput && (
            <button
              type="button"
              className="coach-engagement__search-clear"
              onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
            >
              <i className="fas fa-times" />
            </button>
          )}
        </div>
        <button
          type="button"
          className="coach-engagement__search-btn"
          onClick={() => { setSearch(searchInput); setPage(1); }}
        >
          Search
        </button>
      </div>

      {/* Category + count header */}
      <div className="coach-engagement__list-header">
        <h2 className="coach-engagement__list-title">
          <i className={`fas ${catInfo.icon}`} style={{ color: catInfo.color }} />
          {' '}{catInfo.label} Coaches
          {search && <span className="coach-engagement__search-tag">matching "{search}"</span>}
        </h2>
        {pageInfo.total != null && (
          <span className="coach-engagement__list-count">
            {pageInfo.total} coach{pageInfo.total !== 1 ? 'es' : ''}
          </span>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <Loader />
      ) : error ? (
        <div className="coach-engagement__error">
          <i className="fas fa-exclamation-triangle" />
          <p>{error}</p>
          <button onClick={() => fetchList(page)} className="coach-engagement__retry">Retry</button>
        </div>
      ) : items.length === 0 ? (
        <div className="coach-engagement__empty">
          <i className="fas fa-inbox" />
          <p>No coaches in this category for the selected period</p>
        </div>
      ) : (
        <>
          <div className="coach-engagement__table-wrap">
            <table className="coach-engagement__table">
              <thead>
                <tr>
                  <th>Coach</th>
                  <th>School</th>
                  {category === 'all' && <th>Status</th>}
                  <th>Last Active</th>
                  <th className="coach-engagement__th-num">Views</th>
                  <th className="coach-engagement__th-num">Searches</th>
                  <th className="coach-engagement__th-num">Favs</th>
                  <th className="coach-engagement__th-num">Compares</th>
                  <th className="coach-engagement__th-num">Contacts</th>
                  <th className="coach-engagement__th-num">Health</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const hl = healthLabel(item.healthScore);
                  const name = item.coach.firstName && item.coach.lastName
                    ? `${item.coach.firstName} ${item.coach.lastName}`
                    : '';
                  const itemCat = CATEGORY_MAP[item.category];
                  return (
                    <tr
                      key={item.coach.email}
                      className="coach-engagement__row"
                      onClick={() => history.push(`/admin/coaches/${encodeURIComponent(item.coach.email)}`)}
                    >
                      <td className="coach-engagement__coach-cell">
                        {name && <div className="coach-engagement__coach-name">{name}</div>}
                        <div className={name ? 'coach-engagement__coach-email' : 'coach-engagement__coach-name'}>
                          {item.coach.email}
                        </div>
                      </td>
                      <td>{item.coach.schoolName || '—'}</td>
                      {category === 'all' && (
                        <td>
                          <span
                            className="coach-engagement__cat-badge"
                            style={{ background: itemCat?.color || '#94a3b8' }}
                          >
                            {itemCat?.label || item.category}
                          </span>
                        </td>
                      )}
                      <td
                        className="coach-engagement__time"
                        title={item.lastActiveAt ? new Date(item.lastActiveAt).toLocaleString() : ''}
                      >
                        {timeAgo(item.lastActiveAt)}
                      </td>
                      <td className="coach-engagement__num">{item.counts.views}</td>
                      <td className="coach-engagement__num">{item.counts.searches}</td>
                      <td className="coach-engagement__num">{item.counts.favorites}</td>
                      <td className="coach-engagement__num">{item.counts.compares}</td>
                      <td className="coach-engagement__num">{item.counts.contacts}</td>
                      <td>
                        <span
                          className={`coach-engagement__health coach-engagement__health--${hl.cls}`}
                          title={hl.text}
                        >
                          {item.healthScore}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="coach-engagement__pagination">
              <button type="button" disabled={page <= 1} onClick={() => fetchList(page - 1)}>
                <i className="fas fa-chevron-left" />
              </button>
              <span>Page {page} of {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => fetchList(page + 1)}>
                <i className="fas fa-chevron-right" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CoachEngagementList;
